import type { ColumnsType } from 'antd/es/table';
import { Box, Colors, IcEdit, IcTrash, Pagination, Table, Text, IcSynchronize, IcMenuChange } from 'basic-ui-lib';
import styled from 'styled-components';
import storeApis from 'modules/store/services/apis';
import toast from 'utils/toast';
import { getMessageError } from 'services/response';
import useGetListStores from 'modules/store/services/hooks/useGetListStore';
import ModalConfirm, { useModalConfirmRef } from 'widgets/ModalConfirm';
import { useNavigate } from 'react-router-dom';
import ResetPasswordModal, { useResetPasswordModalRef } from '../ResetPasswordModal';
import { NewStoreModal, useNewStoreModalRef } from 'modules/store/screens/Create';
import { useMediaQuery } from 'react-responsive';
import StoreTableMobile from './StoreTableMobile';

interface DataType {
  storeId: string;
  ownerName: string;
  storeName: string;
  phone: string;
  email: string;
  address: string;
  totalScreen: string;
  createdDate: string;
}

const StoreTable = () => {
  const { storesList, params, mutate } = useGetListStores();
  const modalConfirmRef = useModalConfirmRef();
  const useResetPasswordModal = useResetPasswordModalRef();
  const editStoreModalRef = useNewStoreModalRef();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const navigate = useNavigate();

  const handleDelete = async (storeId: string) => {
    try {
      const res = await storeApis.deleteStore(storeId);

      if (res?.data?.data) {
        toast.success('Store deleted successfully');
        mutate({ ...params });
      }
    } catch (error) {
      toast.error(getMessageError(error) || 'Failed to delete store');
    }
  };

  const handleDetail = (record: DataType) => {
    navigate(`/store/${record.storeId}`, {
      state: {
        storeName: record.storeName,
        ownerName: record.ownerName,
        address: record.address,
        phone: record.phone,
        totalScreen: record.totalScreen,
      }
    });
  };

  const onDeleteStore = (storeId: string) => {
    modalConfirmRef.current?.open(() => handleDelete(storeId), {
      message: isMobile ? 'Do you want to delete this store?' : 'Are you sure you want to delete this store? This action cannot be undone.',
    });
  };

  const onResetPassword = (storeId: string) => {
    useResetPasswordModal.current?.open(storeId);
  };

  const columns: ColumnsType<DataType> = [
    {
      title: 'BUSINESS NAME',
      dataIndex: 'ownerName',
    },
    {
      title: 'NAME',
      dataIndex: 'storeName',
      render: (text, record) => (
        <button onClick={() => handleDetail(record)}>
          <Text content_2 color='primary.6'>
            {text}
          </Text>
        </button>
      ),
    },
    {
      title: 'PHONE',
      dataIndex: 'phone',
    },
    {
      title: 'EMAIL',
      dataIndex: 'email',
      render: (text, record) => (
        <button onClick={() => handleDetail(record)}>
          <Text content_2 color='primary.6'>
            {text}
          </Text>
        </button>
      ),
    },
    {
      title: 'ADDRESS',
      dataIndex: 'address',
    },
    {
      title: 'SCREEN',
      dataIndex: 'totalScreen',
    },
    {
      title: 'CREATED DATE',
      dataIndex: 'createdDate',
    },
    {
      title: 'ACTIONS',
      key: 'action',
      render: (_, record) => (
        <Actions>
          <button type='button' onClick={() => editStoreModalRef.current?.open({ storeId: record.storeId })}>
            <IcEdit fill={Colors.secondary[5]} />
          </button>
          <button type='button' onClick={() => onDeleteStore(record.storeId)}>
            <IcTrash fill={Colors.other[3]} />
          </button>
          <button type='button' className='styled-icon' onClick={() => handleDetail(record)}>
            <IcSynchronize fill={Colors.purple[3]} />
          </button>
          <button type='button' className='styled-icon' onClick={() => onResetPassword(record.storeId)}>
            <IcMenuChange fill={Colors.primary[3]} />
          </button>
        </Actions>
      ),
      width: 100,
      align: 'center',
    },
  ];

  const handlePageChange = (page: number) => {
    mutate({ ...params, page: page });
  };

  if (isMobile) {
    return (
      <>
      <StoreTableMobile
        handleDeleteStore={onDeleteStore}
        handleResetPassword={onResetPassword}
      />
      <ModalConfirm ref={modalConfirmRef} />
      <ResetPasswordModal ref={useResetPasswordModal} />
      </>
    );
  }

  return (
    <TableBox>
      <Table<DataType>
        className='custom-table'
        columns={columns}
        dataSource={storesList?.content}
        scroll={{ x: 'max-content' }}
        pagination={false}
      />
      <Pagination
        totalItems={storesList?.totalElements || 0}
        itemsPerPage={10}
        currentPage={params.page || 1}
        onPageChange={handlePageChange}
        labelDisplay='stores'
        showRowPerPage={true}
        maxVisiblePages={3}
      />
      <ModalConfirm ref={modalConfirmRef} />
      <ResetPasswordModal ref={useResetPasswordModal} />
      <NewStoreModal ref={editStoreModalRef} />
    </TableBox>
  );
};

export default StoreTable;

const TableBox = styled.div`
  width: 100%;
`;

const Actions = styled(Box)`
  display: flex;
  gap: 16px;

  .styled-icon {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;
